import React, { useState, useEffect } from 'react';
import { Layout, Menu, Avatar, Tooltip, Dropdown, Switch, Button, theme, Drawer, Badge } from 'antd';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import {
  DashboardOutlined,
  FolderOutlined,
  BarChartOutlined,
  SafetyOutlined,
  UserOutlined,
  RobotOutlined,
  DeleteOutlined,
  KeyOutlined,
  ExperimentOutlined,
  AppstoreOutlined,
  SecurityScanOutlined,
  FilePdfOutlined,
  LogoutOutlined,
  MoonOutlined,
  SunOutlined,
  UserSwitchOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  MenuOutlined,
  ClockCircleOutlined,
  DatabaseOutlined,
  SettingOutlined,
} from '@ant-design/icons';
import { useAuthStore, useIsAuthenticated } from '../stores/authStore';
import { useThemeStore } from '../stores/themeStore';
import { useQuery } from '@tanstack/react-query';
import { api } from '../api/client';
import { useMediaQuery } from '../hooks/useMediaQuery';
import { MOBILE_MEDIA_QUERY } from '../constants/breakpoints';
import ImpersonationModal from './ImpersonationModal';
import { IS_DEV } from '../utils/appEnv';
import { useInventorySources } from '../hooks/useInventorySources';
import { usePendingProposalsCounts } from '../hooks/usePendingProposalsCounts';
import { usePendingAccessRequestsCount } from '../hooks/usePendingAccessRequestsCount';
import { useLicenseStatus } from '../hooks/useLicenseStatus';
import { useTranslation } from 'react-i18next';

const { Sider } = Layout;

const SIDEBAR_COLLAPSED_KEY = 'sidebar_collapsed';

export const SIDEBAR_WIDTH = 220;
export const SIDEBAR_COLLAPSED_WIDTH = 64;

interface AppSidebarProps {
  collapsed: boolean;
  onCollapse: (collapsed: boolean) => void;
}

const AppSidebar: React.FC<AppSidebarProps> = ({ collapsed, onCollapse }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { token: themeToken } = theme.useToken();
  // Note: `originalUser` (store) intentionally not destructured here — the
  // impersonation banner renders off `confirmedOriginalUser` (derived from
  // the confirmed /auth/me payload below), not the optimistic store value.
  const { logout, isImpersonating, stopImpersonating, setIsImpersonating, setOriginalUser } =
    useAuthStore();
  const themeState = useThemeStore();
  const mode = themeState.mode;
  const toggleMode = themeState.toggleMode;
  const [impersonationModalVisible, setImpersonationModalVisible] = useState(false);
  const isAuthenticated = useIsAuthenticated();
  const isMobile = useMediaQuery(MOBILE_MEDIA_QUERY);
  const [mobileOpen, setMobileOpen] = useState(false);

  const { data: userData } = useQuery({
    queryKey: ['me'],
    queryFn: async () => {
      const response = await api.get('/auth/me');
      return response.data.data;
    },
    enabled: isAuthenticated,
  });

  // SECURITY (GitLab #142 remediation): render the impersonation banner off the
  // SERVER-CONFIRMED /auth/me payload, not the Zustand `isImpersonating` store
  // flag (seeded synchronously from localStorage and only reconciled by the
  // effect below). Gating on `!isMeLoading` alone left two gaps: (a) a
  // one-frame flash of the stale banner before reconciliation runs, and (b) if
  // /auth/me settles into an error state (isLoading:false, isError:true —
  // reachable on a transient 5xx with a still-valid refresh token), nothing
  // ever corrects the stale flag and the wrong banner would persist
  // indefinitely. `userData?.is_impersonating` is only ever `true` once the
  // query has actually resolved successfully with a confirming payload.
  const confirmedIsImpersonating = userData?.is_impersonating === true;
  const confirmedOriginalUser = userData?.original_user ?? null;

  useEffect(() => {
    localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(collapsed));
  }, [collapsed]);

  useEffect(() => {
    const storedIsImpersonating = localStorage.getItem('isImpersonating') === 'true';
    const storedOriginalUser = localStorage.getItem('originalUser');

    if (storedIsImpersonating && storedOriginalUser) {
      try {
        const parsedOriginalUser = JSON.parse(storedOriginalUser);
        setIsImpersonating(true);
        setOriginalUser(parsedOriginalUser);
      } catch {
        // ignore parse errors
      }
    }

    if (userData) {
      if (userData.is_impersonating && userData.original_user) {
        setIsImpersonating(true);
        setOriginalUser(userData.original_user);
      } else if (userData.is_impersonating === false) {
        setIsImpersonating(false);
        setOriginalUser(null);
      }
    }
  }, [userData, setIsImpersonating, setOriginalUser]);

  const { t } = useTranslation('common');
  // Раздел свода показывается, только если в системе есть плагин с capability
  // inventory_source (#276): иначе пункт ведёт на заведомо пустую страницу.
  // `?? []` на время загрузки: пункт лучше показать на секунду позже, чем
  // мигнуть им и убрать.
  const { data: inventorySources } = useInventorySources();
  const hasInventorySource = (inventorySources ?? []).length > 0;

  const isAdmin = userData?.is_admin ?? false;
  // Impersonation: admins only in any environment
  const canImpersonate = isAdmin;

  const { data: licenseStatus } = useLicenseStatus();
  // Периметр доступен, если фича не выключена лицензией (undefined = ещё грузится → показываем).
  const hasPerimeterScan = licenseStatus === undefined || licenseStatus?.features?.perimeter_scan !== false;
  const { data: pendingCounts } = usePendingProposalsCounts();
  // Бэйдж pending-проверок периметра показываем только при наличии фичи (как было
  // на пункте «Периметр» до его удаления) — иначе всегда 0 для нелицензированных.
  const totalPending = hasPerimeterScan
    ? Object.values(pendingCounts ?? {}).reduce((a, b) => a + b, 0)
    : 0;
  const { data: pendingAccessRequestsCount = 0 } = usePendingAccessRequestsCount();

  const handleLogout = async () => {
    await logout();
    navigate('/login');
    setMobileOpen(false);
  };

  // Пути подменю «Настройки» — в одном месте, чтобы раскрытие подменю и
  // подсветка пункта не разъехались.
  const settingsPaths = ['/settings/license', '/settings/llm', '/admin/plugins-management'];

  const getSelectedKey = (): string => {
    const path = location.pathname;
    if (path.startsWith('/admin/users-management')) return '/admin/users-management';
    if (path.startsWith('/service-accounts')) return '/service-accounts';
    if (path.startsWith('/trash')) return '/trash';
    if (path.startsWith('/settings/license')) return '/settings/license';
    if (path.startsWith('/settings/llm')) return '/settings/llm';
    // Без этих двух строк страницы подсвечивали Dashboard: они дошли до
    // меню, но не до разрешения активного пункта.
    if (path.startsWith('/admin/plugins-management')) return '/admin/plugins-management';
    if (path.startsWith('/inventory')) return '/inventory';
    if (path.startsWith('/jobs')) return '/jobs';
    if (path === '/reports' || path.startsWith('/reports/print')) return '/reports';
    if (path.startsWith('/analytics')) return '/analytics';
    // Карточка находки — продолжение «Аналитики», откуда на неё и приходят
    // (#323). Без этой ветки срабатывал общий `return '/dashboard'` в конце, и
    // открыв находку, пользователь видел подсвеченным раздел, в котором не был.
    if (path.startsWith('/findings')) return '/analytics';
    if (path.startsWith('/access-requests')) return '/access-requests';
    // Perimeter no longer has its own menu item — scope/perimeter pages highlight
    // the "Projects" item instead.
    if (path.startsWith('/perimeter')) return '/projects';
    if (path.match(/^\/projects\/[^/]+\/scope/)) return '/projects';
    if (path.startsWith('/projects') || path.startsWith('/products')) return '/projects';
    return '/dashboard';
  };

  const navTo = (path: string) => {
    navigate(path);
    setMobileOpen(false);
  };

  // Group labels are hidden when sidebar is collapsed — Ant Design shows truncated text that looks bad.
  // We pass a non-breaking space so the group renders with no visible label but keeps the spacing.
  const grpLabel = (label: string) => (collapsed ? '\u00A0' : label);

  const mainMenuItems = [
    {
      type: 'group' as const,
      label: grpLabel(t('nav.groupMain')),
      children: [
        { key: '/dashboard', icon: <DashboardOutlined />, label: t('nav.dashboard'), onClick: () => navTo('/dashboard') },
        { key: '/analytics', icon: <BarChartOutlined />, label: t('nav.analytics'), onClick: () => navTo('/analytics') },
        // Свод инвентарных находок (#187). Страница существовала по /inventory
        // с самого начала, но ссылки на неё не было ни в меню, ни откуда-либо
        // ещё: попасть можно было только вводом URL руками, то есть для
        // пользователя фичи не существовало.
        ...(hasInventorySource
          ? [
              {
                key: '/inventory',
                icon: <DatabaseOutlined />,
                label: t('nav.inventoryRollup'),
                onClick: () => navTo('/inventory'),
              },
            ]
          : []),
        { key: '/reports', icon: <FilePdfOutlined />, label: t('nav.reports'), onClick: () => navTo('/reports') },
      ],
    },
    {
      type: 'group' as const,
      label: grpLabel(t('nav.groupWorkspace')),
      children: [
        {
          key: '/projects',
          icon: <FolderOutlined />,
          // Pending perimeter-proposal counts (previously badged on the removed
          // Perimeter menu item) now bubble up onto the Projects item.
          label: (
            <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
              <span>{t('nav.projects')}</span>
              {totalPending > 0 && (
                <Badge count={totalPending} size="small" style={{ flexShrink: 0 }} />
              )}
            </span>
          ),
          onClick: () => navTo('/projects'),
        },
        {
          key: '/access-requests',
          icon: <SafetyOutlined />,
          // Бэйдж — количество pending-заявок к ресурсам, которыми управляет
          // текущий пользователь (см. usePendingAccessRequestsCount).
          label: (
            <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
              <span>{t('nav.accessRequests')}</span>
              {pendingAccessRequestsCount > 0 && (
                <Badge count={pendingAccessRequestsCount} size="small" style={{ flexShrink: 0 }} />
              )}
            </span>
          ),
          onClick: () => navTo('/access-requests'),
        },
        {
          key: '/service-accounts',
          icon: <RobotOutlined />,
          label: t('nav.serviceAccounts'),
          onClick: () => navTo('/service-accounts'),
        },
        // Очередь задач — раздел администратора: маршрут /jobs обёрнут в
        // AdminRoute. Пункт, который у обычного пользователя заведомо
        // упирается в отказ, читается как поломка приложения, поэтому
        // показывается только тем, кто может туда войти (#337). Так же уже
        // сделано для «Пользователей», «Корзины» и «Настроек».
        ...(isAdmin
          ? [
              {
                key: '/jobs',
                icon: <ClockCircleOutlined />,
                label: t('nav.jobs'),
                onClick: () => navTo('/jobs'),
              },
            ]
          : []),
      ],
    },
    ...(isAdmin
      ? [
          {
            type: 'group' as const,
            label: grpLabel(t('nav.groupManagement')),
            children: [
              {
                key: '/admin/users-management',
                icon: <UserOutlined />,
                label: t('nav.users'),
                onClick: () => navTo('/admin/users-management'),
              },
              {
                key: '/trash',
                icon: <DeleteOutlined />,
                label: t('nav.trash'),
                onClick: () => navTo('/trash'),
              },
              // Настройки инстанса — подменю, а не три пункта в общем списке
              // (#275). Лицензия, модель AI-триажа и плагины настраиваются
              // редко, а места в меню занимали столько же, сколько ежедневные
              // разделы. Вложение оставляет в «Управлении» ровно то, чем
              // пользуются регулярно.
              {
                key: 'settings',
                icon: <SettingOutlined />,
                label: t('nav.settings'),
                children: [
                  {
                    key: '/settings/license',
                    icon: <KeyOutlined />,
                    label: t('nav.license'),
                    onClick: () => navTo('/settings/license'),
                  },
                  {
                    key: '/settings/llm',
                    icon: <ExperimentOutlined />,
                    label: t('nav.llmSettings'),
                    onClick: () => navTo('/settings/llm'),
                  },
                  {
                    key: '/admin/plugins-management',
                    icon: <AppstoreOutlined />,
                    label: t('nav.plugins'),
                    onClick: () => navTo('/admin/plugins-management'),
                  },
                ],
              },
            ],
          },
        ]
      : []),
  ];

  const userDropdownItems = [
    {
      key: 'profile',
      label: userData?.email || 'Profile',
      icon: <UserOutlined />,
      onClick: () => navTo('/profile'),
    },
    ...(isImpersonating
      ? [
          { type: 'divider' as const },
          {
            key: 'stop-impersonation',
            label: t('impersonation.stop'),
            icon: <UserSwitchOutlined />,
            onClick: stopImpersonating,
            danger: true,
          },
        ]
      : canImpersonate
        ? [
            { type: 'divider' as const },
            {
              key: 'impersonate',
              label: t('impersonation.start'),
              icon: <UserSwitchOutlined />,
              onClick: () => setImpersonationModalVisible(true),
            },
          ]
        : []),
    { type: 'divider' as const },
    {
      key: 'logout',
      label: t('common.logout'),
      icon: <LogoutOutlined />,
      onClick: handleLogout,
    },
  ];

  if (!isAuthenticated) return null;

  const sidebarContent = (inDrawer = false) => (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        backgroundColor: themeToken.colorBgContainer,
      }}
    >
      {/* Logo */}
      <div
        style={{
          height: 56,
          padding: '0 16px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          ...(IS_DEV
            ? { background: 'linear-gradient(135deg, #7f1d1d 0%, #991b1b 100%)', borderBottom: '1px solid #b91c1c' }
            : { borderBottom: `1px solid ${themeToken.colorBorder}` }),
        }}
      >
        {(!collapsed || inDrawer) ? (
          <Link
            to="/dashboard"
            style={{ display: 'flex', alignItems: 'center', gap: 8, textDecoration: 'none', minWidth: 0, flex: 1 }}
          >
            <div style={{
              width: 28, height: 28, borderRadius: 8, flexShrink: 0,
              background: IS_DEV ? 'rgba(252,165,165,.2)' : 'linear-gradient(135deg, var(--brand-500), var(--brand-700))',
              display: 'grid', placeItems: 'center',
              color: IS_DEV ? '#fca5a5' : '#fff',
              boxShadow: IS_DEV ? 'none' : '0 0 16px var(--brand-glow)',
            }}>
              <SecurityScanOutlined style={{ fontSize: 14 }} />
            </div>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontWeight: 700, fontSize: 13, color: IS_DEV ? '#fef2f2' : themeToken.colorText, whiteSpace: 'nowrap', overflow: 'hidden', lineHeight: 1.2 }}>
                Security Hub
              </div>
              {IS_DEV && (
                <div style={{ fontSize: 10, color: '#fca5a5', fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', lineHeight: 1.2 }}>
                  {t('sidebar.devMode')}
                </div>
              )}
            </div>
          </Link>
        ) : (
          <Tooltip title={IS_DEV ? t('sidebar.appTitleDev') : t('sidebar.appTitle')} placement="right">
            <Link to="/dashboard" style={{ textDecoration: 'none' }}>
              <div style={{
                width: 28, height: 28, borderRadius: 8,
                background: IS_DEV ? 'rgba(252,165,165,.2)' : 'linear-gradient(135deg, var(--brand-500), var(--brand-700))',
                display: 'grid', placeItems: 'center',
                color: IS_DEV ? '#fca5a5' : '#fff',
                boxShadow: IS_DEV ? 'none' : '0 0 16px var(--brand-glow)',
              }}>
                <SecurityScanOutlined style={{ fontSize: 14 }} />
              </div>
            </Link>
          </Tooltip>
        )}
      </div>

      {/* Impersonation Banner */}
      {/* Bug 91 / GitLab #142: render off the confirmed /auth/me payload
          (confirmedIsImpersonating), not the optimistic localStorage-seeded
          store flag — see the comment above the useQuery call for why. */}
      {confirmedIsImpersonating && (!collapsed || inDrawer) && (
        <div
          style={{
            margin: '8px 8px 0',
            padding: '6px 10px',
            backgroundColor: themeToken.colorErrorBg,
            borderRadius: 6,
            border: `1px solid ${themeToken.colorErrorBorder}`,
            fontSize: 11,
            color: themeToken.colorError,
            lineHeight: 1.4,
          }}
        >
          <div style={{ fontWeight: 600 }}>{t('sidebar.impersonationLabel')}</div>
          <div style={{ opacity: 0.85 }}>{userData?.full_name || userData?.email}</div>
          {confirmedOriginalUser && (
            <div style={{ opacity: 0.7 }}>{t('sidebar.impersonationFrom', { name: confirmedOriginalUser.full_name || confirmedOriginalUser.email })}</div>
          )}
        </div>
      )}
      {confirmedIsImpersonating && collapsed && !inDrawer && (
        <Tooltip title={t('sidebar.impersonationTooltip', { name: userData?.full_name || userData?.email })} placement="right">
          <div
            style={{
              margin: '8px auto 0',
              width: 32,
              height: 6,
              borderRadius: 3,
              backgroundColor: themeToken.colorError,
            }}
          />
        </Tooltip>
      )}

      {/* Navigation Menu */}
      <div style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden' }}>
        <Menu
          mode="inline"
          inlineCollapsed={collapsed && !inDrawer}
          // Подменю раскрыто, если открыта его страница: иначе прямой заход по
          // ссылке показывает свёрнутое «Настройки» и не видно, где ты.
          // defaultOpenKeys, а не controlled openKeys: боковая панель монтируется
          // один раз за загрузку приложения, а внутри навигации подменю уже
          // открыто самим кликом.
          defaultOpenKeys={settingsPaths.some((sp) => location.pathname.startsWith(sp)) ? ['settings'] : []}
          selectedKeys={[getSelectedKey()]}
          items={mainMenuItems}
          style={{ border: 'none', paddingTop: 8 }}
          inlineIndent={16}
        />
      </div>

      {/* Bottom: theme + user */}
      <div
        style={{
          borderTop: `1px solid ${themeToken.colorBorder}`,
          padding: collapsed && !inDrawer ? '12px 0' : '12px 16px',
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
          flexShrink: 0,
        }}
      >
        {/* Theme Toggle */}
        {(!collapsed || inDrawer) && mode !== undefined && toggleMode && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '2px 0',
            }}
          >
            <span style={{ fontSize: 12, color: themeToken.colorTextSecondary }}>{t('theme.dark')}</span>
            <Switch
              size="small"
              checked={mode === 'dark'}
              onChange={toggleMode}
              checkedChildren={<MoonOutlined />}
              unCheckedChildren={<SunOutlined />}
            />
          </div>
        )}
        {collapsed && !inDrawer && mode !== undefined && toggleMode && (
          <Tooltip title={mode === 'dark' ? t('theme.light') : t('theme.dark')} placement="right">
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <Switch
                size="small"
                checked={mode === 'dark'}
                onChange={toggleMode}
                checkedChildren={<MoonOutlined />}
                unCheckedChildren={<SunOutlined />}
              />
            </div>
          </Tooltip>
        )}

        {/* Collapse toggle */}
        {!inDrawer && (
          <Tooltip title={collapsed ? t('sidebar.expand') : t('sidebar.collapse')} placement="right">
            <Button
              type="text"
              size="small"
              icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
              onClick={() => onCollapse(!collapsed)}
              style={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: collapsed ? 'center' : 'flex-start',
                gap: 8,
                color: themeToken.colorTextSecondary,
                paddingLeft: collapsed ? 0 : 8,
                height: 32,
              }}
            >
              {!collapsed && <span style={{ fontSize: 12 }}>{t('sidebar.collapseShort')}</span>}
            </Button>
          </Tooltip>
        )}

        {/* User */}
        <Dropdown menu={{ items: userDropdownItems }} placement="topLeft" trigger={['click']}>
          <div
            role="button"
            tabIndex={0}
            aria-haspopup="true"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: collapsed && !inDrawer ? 0 : 10,
              cursor: 'pointer',
              padding: collapsed && !inDrawer ? '6px 0' : '6px 8px',
              borderRadius: 8,
              justifyContent: collapsed && !inDrawer ? 'center' : 'flex-start',
              transition: 'background 0.15s',
            }}
            onMouseEnter={e => {
              (e.currentTarget as HTMLElement).style.background = themeToken.colorFillTertiary;
            }}
            onMouseLeave={e => {
              (e.currentTarget as HTMLElement).style.background = 'transparent';
            }}
            onKeyDown={e => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                (e.currentTarget as HTMLElement).click();
              }
            }}
          >
            <Tooltip
              title={collapsed && !inDrawer ? (userData?.full_name || userData?.email || 'User') : ''}
              placement="right"
            >
              <Avatar
                size={28}
                icon={<UserOutlined />}
                style={{ flexShrink: 0, backgroundColor: themeToken.colorPrimary }}
              />
            </Tooltip>
            {(!collapsed || inDrawer) && (
              <div style={{ overflow: 'hidden', minWidth: 0 }}>
                <div
                  style={{
                    fontSize: 13,
                    fontWeight: 500,
                    color: themeToken.colorText,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    lineHeight: 1.3,
                  }}
                >
                  {userData?.full_name || userData?.email || 'User'}
                </div>
                {userData?.email && userData?.full_name && (
                  <div
                    style={{
                      fontSize: 11,
                      color: themeToken.colorTextSecondary,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      lineHeight: 1.3,
                    }}
                  >
                    {userData.email}
                  </div>
                )}
              </div>
            )}
          </div>
        </Dropdown>
      </div>
    </div>
  );

  // Mobile: hamburger button + drawer
  if (isMobile) {
    return (
      <>
        <Button
          type="text"
          icon={<MenuOutlined />}
          onClick={() => setMobileOpen(true)}
          style={{
            position: 'fixed',
            top: 12,
            left: 12,
            zIndex: 200,
            fontSize: 18,
            width: 40,
            height: 40,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: themeToken.colorBgContainer,
            border: `1px solid ${themeToken.colorBorder}`,
            borderRadius: 8,
            boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
          }}
        />
        <Drawer
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          placement="left"
          width={280}
          styles={{ body: { padding: 0 }, header: { display: 'none' } }}
          style={{ padding: 0 }}
        >
          {sidebarContent(true)}
        </Drawer>
        <ImpersonationModal
          visible={impersonationModalVisible}
          onCancel={() => setImpersonationModalVisible(false)}
        />
      </>
    );
  }

  // Desktop: fixed sidebar
  return (
    <>
      <Sider
        collapsible
        collapsed={collapsed}
        onCollapse={onCollapse}
        trigger={null}
        width={SIDEBAR_WIDTH}
        collapsedWidth={SIDEBAR_COLLAPSED_WIDTH}
        style={{
          overflow: 'hidden',
          height: '100vh',
          position: 'fixed',
          left: 0,
          top: 0,
          bottom: 0,
          zIndex: 100,
          borderRight: `1px solid ${themeToken.colorBorder}`,
          boxShadow: '2px 0 8px rgba(0,0,0,0.04)',
        }}
      >
        {sidebarContent(false)}
      </Sider>
      <ImpersonationModal
        visible={impersonationModalVisible}
        onCancel={() => setImpersonationModalVisible(false)}
      />
    </>
  );
};

export default AppSidebar;
